import { Fragment, ReactNode } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Circle,
  CircleCheckBig,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface Step {
  label: string;
  completed: boolean;
}

interface Props {
  steps: Step[];
  activeStep: number;
  onNext: () => void;
  onBack: () => void;
  isEditMode: boolean;
  stepBody: ReactNode;
}

export default function StepComponent({
  steps,
  activeStep,
  onNext,
  onBack,
  isEditMode,
  stepBody,
}: Props) {
  const isCurrentStepComplete = steps[activeStep].completed;

  return (
    <div className="flex w-full flex-col justify-between gap-8">
      <div className="my-6 flex w-full items-center justify-center">
        {steps.map((step, index) => {
          const isCompleted = step.completed;
          const isActive = activeStep === index;

          return (
            <Fragment key={index}>
              {isCompleted ? (
                <CircleCheckBig className="text-primary" />
              ) : (
                <Circle
                  className="text-primary"
                  fill={isActive ? 'var(--primary-100)' : 'var(--background)'}
                />
              )}
              <p className={cn(isActive && 'font-medium', 'ml-2 text-xs')}>
                {step.label}
              </p>

              {index < steps.length - 1 && (
                <div className="mx-3 h-[1px] w-14 bg-gray-300" />
              )}
            </Fragment>
          );
        })}
      </div>
      <div className="flex w-full overflow-y-hidden rounded border">
        {stepBody}
      </div>
      <div className="bg-background fixed right-0 bottom-0 left-0 z-50 w-full shadow-[0_-4px_12px_rgba(0,0,0,0.08)]">
        <div className="flex max-w-full justify-between px-32 py-5">
          <Button onClick={onBack} type="button" variant="outline">
            <ChevronLeft />
            {activeStep === 0 ? 'Cancel' : 'Previous'}
          </Button>
          <Button
            disabled={!isCurrentStepComplete}
            onClick={onNext}
            type="button"
          >
            {activeStep === steps.length - 1
              ? isEditMode
                ? 'Save Changes'
                : 'Complete'
              : 'Continue'}
            <ChevronRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
